import { FC, ReactElement, ReactNode, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";

import { useRateOptionHighlight } from "../../../RateOptionHighlight/RateOptionHighlightContext";
import { useIsExtrasSectionOpen } from "../ExtrasSection/ExtrasSection";

import { DashboardSwitch } from "@/dashboard-design-system/Switch/DashboardSwitch";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { IconElement } from "@/icons/types";

export type RateOptionKey =
  | "schedule"
  | "memberOnly"
  | "payWhatYouCan"
  | "groupTicket"
  | "categories"
  | "taxReceipt"
  | "tax";

interface RateOptionToggleRowProps {
  optionKey: RateOptionKey;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  icon?: IconElement;
  label: ReactNode;
  ariaLabel?: string;
  tooltip?: ReactNode;
  inlineConfig?: ReactNode;
  belowContent?: ReactNode;
  belowContentGap?: number;
  renderSwitch?: (control: ReactElement) => ReactNode;
  "data-test"?: string;
}

const RIPPLE_DURATION_MS = 1400;

const rippleCircleSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: 220,
  height: 220,
  borderRadius: "50%",
  transform: "translate(-50%, -50%) scale(0)",
  opacity: 0,
  zIndex: -1,
  pointerEvents: "none",
  willChange: "transform, opacity",
  "@media (prefers-reduced-motion: reduce)": { display: "none" },
} as const;

export const RateOptionToggleRow: FC<RateOptionToggleRowProps> = ({
  optionKey,
  checked,
  onToggle,
  disabled,
  disabledTooltip,
  icon,
  label,
  ariaLabel,
  tooltip,
  inlineConfig,
  belowContent,
  belowContentGap = 0.75,
  renderSwitch,
  "data-test": dataTest,
}) => {
  const theme = useTheme();
  const isExtrasOpen = useIsExtrasSectionOpen();
  const { isPending: isHighlightPending, clear: clearHighlight } = useRateOptionHighlight(optionKey);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const hasRippleStartedRef = useRef(false);
  const isHighlighting = isHighlightPending && isExtrasOpen;
  const accessibleName = ariaLabel ?? (typeof label === "string" ? label : undefined);

  useEffect(() => {
    if (!isHighlighting) {
      return;
    }
    hasRippleStartedRef.current = false;
    const scrollTimeout = setTimeout(() => {
      rowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, theme.transitions.duration.standard);
    const clearTimeoutId = setTimeout(clearHighlight, theme.transitions.duration.standard + RIPPLE_DURATION_MS + 600);
    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(clearTimeoutId);
      if (hasRippleStartedRef.current) {
        clearHighlight();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlighting]);

  const switchControl = (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <DashboardSwitch
        size="small"
        checked={checked}
        onChange={(event) => onToggle(event.target.checked)}
        disabled={disabled}
        inputProps={accessibleName ? { "aria-label": accessibleName } : undefined}
        data-test={dataTest ?? `rate-option-switch-${optionKey}`}
      />
      {isHighlighting && (
        <>
          <Box
            component="span"
            aria-hidden
            onAnimationStart={() => {
              hasRippleStartedRef.current = true;
            }}
            onAnimationEnd={clearHighlight}
            sx={{
              ...rippleCircleSx,
              background: `radial-gradient(circle, ${theme.palette.surface.brand.moderate} 0%, ${theme.palette.surface.brand.quiet} 55%, transparent 85%)`,
              animation: `rateOptionGlow ${RIPPLE_DURATION_MS}ms ease-out 1 both`,
              "@keyframes rateOptionGlow": {
                "0%": { transform: "translate(-50%, -50%) scale(0)", opacity: 0 },
                "10%": { opacity: 1 },
                "50%": { opacity: 0.6 },
                "80%": { opacity: 0.25 },
                "100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0 },
              },
            }}
          />
          <Box
            component="span"
            aria-hidden
            sx={{
              ...rippleCircleSx,
              background: `radial-gradient(circle, transparent 0%, transparent 55%, ${alpha(theme.palette.surface.brand.intense, 0.28)} 72%, ${alpha(theme.palette.surface.brand.intense, 0.55)} 84%, ${alpha(theme.palette.surface.brand.intense, 0.2)} 92%, transparent 98%)`,
              animation: `rateOptionWave ${RIPPLE_DURATION_MS}ms ease-out 1 both`,
              "@keyframes rateOptionWave": {
                "0%": { transform: "translate(-50%, -50%) scale(0)", opacity: 1 },
                "10%": { opacity: 0.9 },
                "50%": { opacity: 0.55 },
                "80%": { opacity: 0.25 },
                "100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0 },
              },
            }}
          />
        </>
      )}
    </Box>
  );

  return (
    <Box
      ref={rowRef}
      data-test={`rate-option-row-${optionKey}`}
      sx={{
        paddingX: 2,
        paddingY: 1,
        minHeight: 46,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        backgroundColor: checked ? theme.palette.surface.neutral.white : "transparent",
        transition: theme.transitions.create("background-color", {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          columnGap: 3,
          rowGap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: disabled ? 0.5 : 1 }}>
          {renderSwitch ? (
            renderSwitch(switchControl)
          ) : disabled && disabledTooltip ? (
            <DashboardTooltip title={disabledTooltip}>
              <Box sx={{ display: "inline-flex" }}>{switchControl}</Box>
            </DashboardTooltip>
          ) : (
            switchControl
          )}
          {icon ? (
            <DashboardIcon size="small" vibe="text-neutral-moderate">
              {icon}
            </DashboardIcon>
          ) : null}
          <DashboardTypography component="span" variant="caption" vibe="text-neutral-moderate">
            {label}
          </DashboardTypography>
          {tooltip ? <DashboardInformationTooltip title={tooltip} placement="top" /> : null}
        </Box>
        {inlineConfig ? (
          <Box sx={{ pl: 4.5, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>{inlineConfig}</Box>
        ) : null}
      </Box>
      {belowContent ? <Box sx={{ marginTop: belowContentGap }}>{belowContent}</Box> : null}
    </Box>
  );
};
